/**
 * Limsa Recs Loader — Force-load product-recommendations (YMAL + complementary)
 * Problem: Shopify native <product-recommendations> custom element mobilde
 *          içeriği inject etmiyor (IntersectionObserver/scroll tetiklenmiyor).
 * Solution: DOMContentLoaded'da ve aralıklı olarak hepsini manuel fetch edip inject et.
 * Author: Batuhan/Claude — 2026-04-18
 */
(function () {
  'use strict';

  var ATTR_LOADED = 'data-limsa-recs-loaded';

  function logErr(msg, e) {
    try { console.warn('[limsa-recs]', msg, e && e.message); } catch (_) {}
  }

  function fixImgs(root) {
    if (!root) return;
    root.querySelectorAll('img').forEach(function (img) {
      // data-src → src (lazy lib'ler için)
      var ds = img.getAttribute('data-src');
      if (ds && (!img.src || img.src === window.location.href)) {
        img.setAttribute('src', ds);
      }
      // data-srcset → srcset
      var dss = img.getAttribute('data-srcset');
      if (dss && !img.getAttribute('srcset')) {
        img.setAttribute('srcset', dss);
      }
      // lazy → eager
      if (img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
      }
      // Opacity/visibility zorla (bazı theme'ler 0 bırakıyor)
      img.style.opacity = '1';
      img.style.visibility = 'visible';
      img.style.display = 'block';
      // Load error fallback: kırık img'lerde placeholder
      if (!img.dataset.limsaImgPatched) {
        img.dataset.limsaImgPatched = '1';
        img.addEventListener('error', function () {
          this.style.minHeight = '200px';
          this.style.background = 'linear-gradient(135deg,#f0ebe4,#e5ded3)';
        });
        // Force load: src'yi yeniden set et
        if (img.src) {
          var s = img.src;
          img.src = '';
          img.src = s;
        }
      }
    });
    /* Card-wrapper ve slide visibility */
    root.querySelectorAll('.card-wrapper, .grid__item, .swiper-slide').forEach(function (el) {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
    });
  }

  function initSwiperIn(root) {
    if (!root || !window.Swiper) return;
    var candidates = root.querySelectorAll(
      '.swiper:not(.swiper-initialized), .swiper-container:not(.swiper-initialized)'
    );
    candidates.forEach(function (el) {
      try {
        new window.Swiper(el, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          grabCursor: true,
          watchOverflow: true,
          navigation: {
            nextEl: el.querySelector('.swiper-button-next'),
            prevEl: el.querySelector('.swiper-button-prev')
          },
          pagination: el.querySelector('.swiper-pagination')
            ? { el: el.querySelector('.swiper-pagination'), clickable: true }
            : undefined,
          breakpoints: {
            640: { slidesPerView: 2.4, spaceBetween: 14 },
            768: { slidesPerView: 3, spaceBetween: 16 },
            990: { slidesPerView: 4, spaceBetween: 18 }
          }
        });
      } catch (e) { logErr('swiper init', e); }
    });
  }

  function loadOne(el) {
    if (!el || el.getAttribute(ATTR_LOADED) === '1') return;
    var url = el.getAttribute('data-url');
    if (!url) return;
    // Skip already populated ones (Shopify native beat us to it — good)
    if (el.querySelector('.card-wrapper, .grid__item, recommendation-slider, .swiper')) {
      el.setAttribute(ATTR_LOADED, '1');
      fixImgs(el);
      initSwiperIn(el);
      el.classList.add('limsa-recs-ready');
      return;
    }
    el.setAttribute(ATTR_LOADED, '1');

    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    fetch(url + sep + 'limsa=1&_=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html.trim();

        // Response genellikle: <section>...<product-recommendations>...</product-recommendations>...</section>
        var innerTag = tmp.querySelector('product-recommendations');
        var contentHTML = '';
        if (innerTag && innerTag.innerHTML.trim().length > 0) {
          contentHTML = innerTag.innerHTML;
        } else {
          // fallback: tüm section inner
          var sec = tmp.querySelector('.shopify-section');
          contentHTML = sec ? sec.innerHTML : tmp.innerHTML;
        }
        if (!contentHTML || contentHTML.trim().length < 20) return;

        el.innerHTML = contentHTML;
        el.removeAttribute('hidden');
        el.classList.add('limsa-recs-ready', 'product-recommendations--loaded');

        fixImgs(el);
        fixCardLayout(el);
        setTimeout(function () { initSwiperIn(el); fixCardLayout(el); }, 60);
        setTimeout(function () { initSwiperIn(el); fixCardLayout(el); }, 400);
      })
      .catch(function (err) { logErr('fetch failed for ' + url, err); });
  }

  /* CARD LAYOUT FIX — Shopify CSS minifier position/inset/transform override'larını siliyor.
     Inline style ile ZORLA set et (minifier dokunmaz).
     1. card__inner içindeki overlay .card__information'ı gizle (duplicate heading)
     2. .for-arrow-alignment card__content → normal flow (position:relative, top/bottom:auto)
     3. Img object-position: center top (Shopify default yüz korunur) */
  function fixCardLayout(root) {
    if (!root || !root.querySelectorAll) return;

    // 1) Duplicate heading gizle (overlay içindeki card__information)
    root.querySelectorAll('.card__inner > .card__content:not(.for-arrow-alignment) > .card__information').forEach(function (el) {
      el.style.display = 'none';
    });

    // 2) for-arrow-alignment card__content + içindeki card__information → normal flow
    var staticFlowStyles =
      'position: relative !important;' +
      'top: auto !important;' +
      'bottom: auto !important;' +
      'left: auto !important;' +
      'right: auto !important;' +
      'inset: auto !important;' +
      'transform: none !important;' +
      'display: block !important;' +
      'background: transparent !important;' +
      'backdrop-filter: none !important;' +
      '-webkit-backdrop-filter: none !important;' +
      'max-height: none !important;' +
      'text-align: left !important;' +
      'width: 100% !important;' +
      'box-sizing: border-box !important;';
    root.querySelectorAll('.card__content.for-arrow-alignment').forEach(function (el) {
      el.style.cssText = staticFlowStyles + 'padding: 8px 10px 12px !important;';
    });
    root.querySelectorAll('.card__content.for-arrow-alignment .card__information').forEach(function (el) {
      el.style.cssText = staticFlowStyles + 'padding: 0 !important; margin: 0 !important;';
    });

    // 3) Img object-position Shopify default (center top) — yüz korunur
    root.querySelectorAll('.card__media img, .card__inner img').forEach(function (img) {
      img.style.setProperty('object-position', 'center top', 'important');
      img.style.setProperty('object-fit', 'cover', 'important');
    });

    // 4) Fiyat görünür + pembe
    root.querySelectorAll('.card__content.for-arrow-alignment .price, .card__content.for-arrow-alignment .price__regular, .card__content.for-arrow-alignment .price-item').forEach(function (el) {
      el.style.setProperty('display', 'block', 'important');
      el.style.setProperty('color', '#fc5590', 'important');
      el.style.setProperty('font-weight', '600', 'important');
      el.style.setProperty('margin', '4px 0 0', 'important');
    });

    // 5) YMAL başlığı "You may also like" pembe (section içinde)
    var heading = document.querySelector('product-recommendations .product-recommendations__heading, .product-recommendations__heading');
    if (heading) heading.style.setProperty('color', '#fc5590', 'important');
  }

  function loadAll() {
    var nodes = document.querySelectorAll('product-recommendations[data-url]');
    nodes.forEach(loadOne);
    /* Zaten yüklü olanlar için de layout fix */
    fixCardLayout(document);
  }

  function schedule() {
    // Eager'leri hemen başlat
    loadAll();
    // Shopify native'in işini bitirmesi için retry
    setTimeout(loadAll, 800);
    setTimeout(loadAll, 2000);
    setTimeout(loadAll, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // SPA-like navigation (some themes) — MutationObserver ile yeni eklenen rec'leri yakala
  try {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.tagName && n.tagName.toLowerCase() === 'product-recommendations') {
            loadOne(n);
          } else if (n.querySelectorAll) {
            n.querySelectorAll('product-recommendations[data-url]').forEach(loadOne);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) { logErr('mutation observer', e); }
})();
